import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  PriorityHigh as PriorityHighIcon,
  LowPriority as LowPriorityIcon,
  ReportProblem as MediumPriorityIcon,
} from "@mui/icons-material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MUITypography from "../../Components/MUITypography";
import { align_center, align_row, VM_Colors } from "../../Styles/VMStyles";
import { ROUTE_UPDATE_CHECK_LIST, STATUS_COMPLETED } from "../../Util/VMConstants";
import TodayUpdateItem from "./TodayUpdateItem";

const priorityIcons = {
  High: <PriorityHighIcon sx={{ color: VM_Colors.error, fontSize: 18 }} />,
  Medium: <MediumPriorityIcon sx={{ color: VM_Colors.lead, fontSize: 18 }} />,
  Low: <LowPriorityIcon sx={{ color: VM_Colors.dark_green, fontSize: 18 }} />,
};

const isChecklistCompleted = (status) => {
  const s = (status || "").trim();
  return s === STATUS_COMPLETED || s === "Complete";
};

export const ChecklistSection = ({ data = [] }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const pageSize = 5;

  const totalChecklists = data.length;
  const completedCount = useMemo(
    () =>
      data.filter((item) => isChecklistCompleted(item.checklist_status)).length,
    [data]
  );

  const activeChecklists = useMemo(
    () => data.filter((item) => !isChecklistCompleted(item.checklist_status)),
    [data]
  );

  const progress =
    totalChecklists > 0
      ? Math.round((completedCount / totalChecklists) * 100)
      : 0;

  const totalPages = Math.ceil(activeChecklists.length / pageSize);
  const startIndex = page * pageSize;
  const currentChecklists = activeChecklists.slice(
    startIndex,
    startIndex + pageSize
  );

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        minWidth: "250px",
        borderColor: VM_Colors.light_gray,
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.04)",
      }}
    >
      <CardContent
        sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2.25 }}
      >
        <MUITypography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
          Today&apos;s Checklist
        </MUITypography>

        {totalChecklists === 0 ? (
          <Box
            sx={{
              ...align_center,
              ...align_row,
              width: "100%",
              height: "100%",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              component="img"
              src="/static/task_cancel.svg"
              alt="Checklist"
              sx={{ mt: 5, height: { xs: 50, md: 50 } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 1 }}>
              There are no Checklists Yet.
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  borderRadius: 5,
                  height: 7,
                  bgcolor: "#E8EEF8",
                  "& .MuiLinearProgress-bar": { bgcolor: VM_Colors.primary },
                }}
              />
              <Typography variant="caption" sx={{ color: VM_Colors.primary, fontWeight: 600 }}>
                {completedCount}/{totalChecklists} Completed
              </Typography>
            </Box>

            {activeChecklists.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ flexGrow: 1, py: 2 }}
              >
                All checklists are completed.
              </Typography>
            ) : (
              <>
                <Box sx={{ flexGrow: 1 }}>
                  {currentChecklists.map((item, idx) => (
                    <TodayUpdateItem
                      key={item.id || idx}
                      title={item.checklist_name}
                      status={item.checklist_status}
                      createdDate={item.created_date}
                      dueDate={item.due_date}
                      createdBy={item.created_by}
                      assignedTo={item.assign_users}
                      leading={
                        item.priority ? (
                          <Tooltip title={`Priority: ${item.priority}`} arrow>
                            <Box sx={{ mt: "1px" }}>{priorityIcons[item.priority]}</Box>
                          </Tooltip>
                        ) : null
                      }
                      onClick={() =>
                        navigate("/" + ROUTE_UPDATE_CHECK_LIST, {
                          state: { checklistId: item.id },
                        })
                      }
                    />
                  ))}
                </Box>

                {totalPages > 1 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={2}
                    mt={1}
                  >
                    <IconButton onClick={handlePrev} disabled={page === 0}>
                      <ChevronLeft size={20} />
                    </IconButton>
                    <Typography variant="caption">
                      Page {page + 1} of {totalPages}
                    </Typography>
                    <IconButton
                      onClick={handleNext}
                      disabled={page === totalPages - 1}
                    >
                      <ChevronRight size={20} />
                    </IconButton>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
